// Diary mood taxonomy — single source of truth for emoji + label + category.
//
// Per `architecture.md §8.7` + ADR-0014: 6 categories × 5 levels = 30 locked
// mood codes. The locked codes (e.g. ``mood_happy_3``) match the backend
// ``app/core/mood_codes.py::DIARY_MOODS`` constant byte-for-byte (that is the
// definition; ``diary/schemas.py`` re-exports it), so this file and
// ``app/core/mood_codes.py`` have to move together. Backend
// ``app/modules/diary/service/classify.py::classify_mood_for`` runs
// the Laya SUGGEST and returns one of these codes.
//
// Per W2.3 frontend spec §6.3.6 + DoD for outcome #3: this module is the
// ONLY place where the 30 emoji / 繁體中文 label / level label live.
// ``MoodEmojiPicker`` imports from here. Adding a new mood requires an ADR
// (per ADR-0014 §Alternatives + ADR-0015 §Harder: taxonomy additions go
// through ADR).

export const MOOD_CATEGORIES = ["excited", "happy", "calm", "sad", "angry", "other"] as const;

export type MoodCategory = (typeof MOOD_CATEGORIES)[number];

export const MOOD_LEVELS = [1, 2, 3, 4, 5] as const;

export type MoodLevel = (typeof MOOD_LEVELS)[number];

/**
 * All 30 locked mood codes, ordered category-major × level-minor.
 * Length MUST be 30 (architecture §8.7 + ADR-0014).
 */
export const MOOD_CODES: readonly string[] = Object.freeze(
  MOOD_CATEGORIES.flatMap((cat) => MOOD_LEVELS.map((lvl) => `mood_${cat}_${lvl}` as const)),
);

if (MOOD_CODES.length !== 30) {
  throw new Error(`MOOD_CODES must be 30 (per architecture §8.7); got ${MOOD_CODES.length}`);
}

/**
 * Emoji per (category, level). CJK-heavy per heshe emotional-design spec.
 * Index = level - 1.
 */
export const MOOD_EMOJI: Readonly<Record<MoodCategory, readonly string[]>> = Object.freeze({
  excited: ["🙂", "😀", "😆", "🤩", "🥳"],
  happy: ["🙂", "😊", "😄", "😁", "🤗"],
  calm: ["😶", "🙂", "😌", "😇", "🧘"],
  sad: ["😕", "😟", "😢", "😭", "💔"],
  angry: ["😑", "😠", "😡", "🤬", "🥵"],
  other: ["🤔", "😐", "😶", "🌫", "❓"],
});

/**
 * Simplified 6-emoji taxonomy (W5 refactor: 30 → 6).
 * One canonical emoji per category. Used in overview list + detail header.
 * Mood code level is fixed to 3 (middle) when writing from the simplified picker.
 */
export const EMOJI_FOR_CATEGORY: Readonly<Record<MoodCategory, string>> = Object.freeze({
  excited: "🤩",
  happy: "😊",
  calm: "😌",
  sad: "😢",
  angry: "😡",
  other: "🤔",
});

/**
 * 繁體中文 category labels — used in tooltips + aria-label so screen readers
 * don't hear English codes (per AGENTS.md §3 #14 繁體中文 only).
 */
export const MOOD_CATEGORY_LABEL_ZH: Readonly<Record<MoodCategory, string>> = Object.freeze({
  excited: "興奮",
  happy: "開心",
  calm: "平靜",
  sad: "悲傷",
  angry: "煩躁",
  other: "其他",
});

/**
 * 繁體中文 level labels — index = level - 1.
 */
export const MOOD_LEVEL_LABEL_ZH: readonly string[] = Object.freeze([
  "至少",
  "輕度",
  "中度",
  "強烈",
  "非常強烈",
]);

/**
 * Build a locked mood code from category + level.
 *
 * @example moodCode("happy", 3) === "mood_happy_3"
 */
export function moodCode(category: MoodCategory, level: MoodLevel): string {
  return `mood_${category}_${level}`;
}

/**
 * Parse a locked mood code back into its (category, level) tuple.
 * Returns ``null`` for malformed codes (forward-compat for taxonomy expansion).
 *
 * @example parseMoodCode("mood_happy_3") === ["happy", 3]
 */
export function parseMoodCode(code: string): [MoodCategory, MoodLevel] | null {
  const m = /^mood_([a-z]+)_([1-5])$/.exec(code);
  if (!m) return null;
  const cat = m[1] as MoodCategory;
  const lvl = Number(m[2]) as MoodLevel;
  if (!MOOD_CATEGORIES.includes(cat) || !MOOD_LEVELS.includes(lvl)) return null;
  return [cat, lvl];
}

/**
 * Get the emoji for a locked mood code, or ``"❓"`` for malformed codes.
 * Uses simplified 6-emoji taxonomy (W5): only category matters, level is ignored.
 */
export function emojiForCode(code: string | null | undefined): string {
  if (!code) return "❓";
  const parsed = parseMoodCode(code);
  if (!parsed) return "❓";
  const [cat] = parsed;
  return EMOJI_FOR_CATEGORY[cat] ?? "❓";
}

/**
 * Get the 繁中 human-readable label for a mood code, e.g.
 * ``"中度開心"`` for ``"mood_happy_3"``.
 *
 * Used in the mood-picker status row + dashboard widget.
 */
export function moodLabelZh(code: string | null | undefined): string {
  if (!code) return "";
  const parsed = parseMoodCode(code);
  if (!parsed) return code;
  const [cat, lvl] = parsed;
  return `${MOOD_LEVEL_LABEL_ZH[lvl - 1]}${MOOD_CATEGORY_LABEL_ZH[cat]}`;
}
